import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CURRENT_MODEL_VERSION } from '@shared/constants/model-version';
import {
  AccuracyMetricsByModelVersionID,
  GameID,
  GamePredictionRecordsByGameID,
  IAccuracyMetrics,
  IGamePrediction,
  PredictionSliceProps,
  UpdateAccuracyMetricsByModelVersionPayload,
} from '@shared/types';
import { Cls, Emo } from 'app/constants';
import { PREDICTION_MODEL_VERSIONS } from 'constants/predictionmodel-versions';
import { memoize } from 'proxy-memoize';
import { RootState } from 'types';

// const initialAccuracy: IAccuracy = {
//   totalPredictions: 0,
//   correctPredictions: 0,
//   accuracyPercentage: 0,
// };

const initialState: PredictionSliceProps = {
  predictions: {},
  accuracyMetrics: [],
  accuracyMetricsByModelVersionID: {} as AccuracyMetricsByModelVersionID,
  // IAccuracyMetrics
  // {
  //   modelVersion: '',
  //   totalPredictions: 0,
  //   correctPredictions: 0,
  //   accuracyPercentage: 0, // Overall accuracy %
  //   byConfidence: {
  //     high: initialAccuracy,
  //     medium: initialAccuracy,
  //     low: initialAccuracy,
  //   },
  //   byGameType: {
  //     division: initialAccuracy,
  //     conference: initialAccuracy,
  //     interconference: initialAccuracy,
  //   },
  //   bySeason: {},
  //   bySeasonWeek: {},
  // },
  // Loading state
  isLoading: false,
  error: undefined,
  selectedModelVersion: CURRENT_MODEL_VERSION,
};

export const predictionsSlice = createSlice({
  name: 'predictions',
  initialState,
  reducers: {
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setSelectedModelVersion: (state, action: PayloadAction<string>) => {
      state.selectedModelVersion = action.payload;
    },
    setPredictions: (state, action: PayloadAction<GamePredictionRecordsByGameID>) => {
      state.predictions = action.payload;
    },
    setAccuracyMetrics: (state, action: PayloadAction<IAccuracyMetrics[]>) => {
      state.accuracyMetrics = action.payload;
    },
    addPrediction: (state, action: PayloadAction<Record<GameID, IGamePrediction>>) => {
      state.predictions = { ...state.predictions, ...action.payload };
    },
    addAccuracyMetric: (state, action: PayloadAction<IAccuracyMetrics>) => {
      state.accuracyMetrics.push(action.payload);
    },
    setAccuracyMetricsByModelVersionID: (state, action: PayloadAction<AccuracyMetricsByModelVersionID>) => {
      state.accuracyMetricsByModelVersionID = action.payload;
    },
    updateAccuracyMetrics: (state, action: PayloadAction<UpdateAccuracyMetricsByModelVersionPayload>) => {
      const temp = [...state.accuracyMetrics];
      const index = temp.findIndex((metric) => metric.modelVersion === action.payload.modelVersion);
      if (index !== -1) {
        temp[index] = { ...temp[index], ...action.payload.accuracyMetrics };
        state.accuracyMetrics = temp;
      } else {
        const msg = 'Trying to update non-existing accuracy metrics for model';
        console.warn(`${Emo.warning} ${msg} version %c ${action.payload.modelVersion} %c `, Cls.Cri);
        return state;
      }
    },
    setError: (state, action: PayloadAction<string | undefined>) => {
      state.error = action.payload;
    },
    resetGamesConfig: () => {
      return { ...initialState };
    },
  },
});

export const {
  // setSelectedScene,
  setIsLoading,
  setSelectedModelVersion,
  setAccuracyMetrics,
  setAccuracyMetricsByModelVersionID,
  setError,
  setPredictions,
  addPrediction,
  updateAccuracyMetrics,
  addAccuracyMetric,
  resetGamesConfig,
} = predictionsSlice.actions;

// #region UI Selectors *********************************************************
// export const getSelectedScene = (state: RootState): Scene => state.nfl.sceneSelected;
export const getAllPredictions = (state: RootState): GamePredictionRecordsByGameID => state.predictions.predictions;
export const getSelectedModelVersion = (state: RootState): string | undefined => state.predictions.selectedModelVersion;
export const getAccuracyMetrics = (state: RootState): IAccuracyMetrics[] => state.predictions.accuracyMetrics;
export const getPredictionError = (state: RootState): string | undefined => state.predictions.error;
export const getIsLoading = (state: RootState): boolean => state.predictions.isLoading;

// Memoized selector - auto-tracks state.predictions.accuracyMetrics access
export const getAvailablePredictionModelVersionsWithAccuracyMetrics = memoize((state: RootState): string[] =>
  state.predictions.accuracyMetrics.map((metric) => metric.modelVersion)
);

export const getPredictionByGameID = (state: RootState, gameID: GameID): IGamePrediction | undefined =>
  state.predictions.predictions[gameID];

export const getAllDefinedModelVersions = (): string[] => Object.keys(PREDICTION_MODEL_VERSIONS);

export const getAccuracyMetricsBySelectedModelVersionID = (state: RootState): IAccuracyMetrics =>
  state.predictions.accuracyMetricsByModelVersionID[state.predictions.selectedModelVersion || CURRENT_MODEL_VERSION];

export const getAccuracyMetricsByModelVersionID = (state: RootState, modelVersion: string): IAccuracyMetrics =>
  state.predictions.accuracyMetricsByModelVersionID[modelVersion];
// #endregion

export default predictionsSlice.reducer;
